import type { IndividuoArboreo } from "@/types/ui-models";
import { useNavigate } from "react-router-dom";
import TreeRiskBadge from "./TreeRiskBadge";
import TreeReviewBadge from "./TreeReviewBadge";
import { tipoIndividuoLabel } from "@/config/cta-mapa-config";

interface TreeCardProps {
  individuo: IndividuoArboreo;
}

const riscoBorder: Record<string, string> = {
  BAIXO: "border-l-primary-400",
  MEDIO: "border-l-caution-500",
  ALTO: "border-l-warning-500",
  CRITICO: "border-l-danger-500",
};

function formatDate(value: string): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

/**
 * Cartao do exemplar com o que a listagem do cadastro realmente traz.
 * Medidas, condicao e recomendacao ficam na FCA (vem da vistoria validada),
 * por isso nao aparecem aqui como zero.
 */
export default function TreeCard({ individuo }: TreeCardProps) {
  const navigate = useNavigate();
  const border = riscoBorder[individuo.classeRisco?.toUpperCase()] ?? "border-l-background-300";
  const endereco = [individuo.logradouro, individuo.bairro].filter(Boolean).join(" — ");

  return (
    <div className={`bg-white rounded-card border border-l-2 ${border} p-5 hover:shadow-md transition-all duration-200`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-mono text-foreground-500 tracking-tight">{individuo.codigoCta || "—"}</p>
          <h3 className="text-sm font-semibold text-foreground-900 font-heading mt-0.5">
            {individuo.nomePopular || "Espécie não identificada"}
          </h3>
          {individuo.nomeCientifico && <p className="text-xs text-foreground-400 italic">{individuo.nomeCientifico}</p>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <TreeRiskBadge risco={individuo.classeRisco} />
          <TreeReviewBadge status={individuo.statusRevisao} />
        </div>
      </div>

      <div className="space-y-1.5 mb-3">
        <div className="flex items-center gap-1.5 text-xs text-foreground-600">
          <i className="ri-map-pin-line text-foreground-400 text-[10px]"></i>
          <span className="truncate">{endereco || "Sem endereço informado"}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-foreground-600">
          <i className="ri-folder-chart-line text-foreground-400 text-[10px]"></i>
          <span className="truncate">{individuo.projeto || "—"}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-foreground-600">
          <i className="ri-plant-line text-foreground-400 text-[10px]"></i>
          <span>{tipoIndividuoLabel(individuo.tipo)}</span>
        </div>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-foreground-500 mb-3">
        <i className="ri-calendar-line text-[10px]"></i>
        <span>Última vistoria: {formatDate(individuo.ultimaVistoria)}</span>
      </div>

      <button onClick={() => navigate(`/cta/${individuo.id}`)} className="btn-primary text-xs px-2 py-1.5 w-full cursor-pointer">
        <i className="ri-file-chart-line text-[10px]"></i>
        Ver FCA
      </button>
    </div>
  );
}
